import type { ComponentType } from 'react';
import { getWidgetThumbnailComponentSync } from '@/lib/config/agents.config';

/**
 * Thumbnail mount contract: every widget accepts `data` plus a `mode`
 * discriminator (see `WidgetRenderProps`). In `'thumbnail'` mode the widget
 * early-returns its store-free preview, so a single prop-only mount covers
 * every supported kind.
 */
export interface WidgetThumbnailComponentProps {
  data: unknown;
  mode: 'thumbnail';
}

/**
 * Resolves the thumbnail-capable widget component for an artifact's widget
 * `key`. Only widgets with a co-located `*Thumbnail` variant resolve (see
 * `WIDGET_THUMBNAIL_KEYS` in agents.config); every other key — unmapped kinds
 * AND registered widgets that have no thumbnail (image/video/template
 * generation, etc.) — returns `null` so the caller falls back to a media
 * thumbnail or the palette icon. Critically this does NOT reuse
 * `getWidgetComponentSync`'s agent `component` fallback, which would mount a
 * full interactive widget in the thumbnail slot (nested-<button> hydration
 * error inside the card's clickable button).
 *
 * Kept as a plain resolver (not a hook) so it can be unit-tested in isolation
 * and called from a `useMemo` at the mount site.
 */
export function resolveWidgetThumbnailComponent(
  artifactKey: string | null | undefined,
): ComponentType<WidgetThumbnailComponentProps> | null {
  const component = getWidgetThumbnailComponentSync(artifactKey);
  if (!component) return null;
  // The registry types widgets as `ComponentType<unknown>` (each widget's
  // `data` shape differs); narrow to the thumbnail mount contract at this
  // single seam — same cast the preview modal and side panel perform.
  return component as ComponentType<WidgetThumbnailComponentProps>;
}
